/**
 * Conversation Labels Component
 *
 * Display of conversation labels as colored pills in the conversation list.
 * Font size matches the list name column (14px).
 */

import React from 'react';
import { Box, Tooltip, type SxProps, type Theme } from '@mui/material';
import { colorTokens } from '@/widgets/frontdesk/design-system/tokens';

export interface ConversationLabel {
  id?: number | string;
  title: string;
  color?: string;
}

export interface ConversationLabelsProps {
  labels: string[] | ConversationLabel[];
  accountLabels?: ConversationLabel[];
  maxLabels?: number;
  sx?: SxProps<Theme>;
}

const DEFAULT_LABEL_COLOR = '#EF4444';
const GMAIL_LABEL_COLOR = '#EA4335';
/** Amber chip for auto "Snoozed for …" duration labels (readable on white rows). */
const SNOOZE_DURATION_LABEL_BG = colorTokens.warning[100];
const SNOOZE_DURATION_LABEL_TEXT = colorTokens.warning[800];
const SNOOZE_DURATION_LABEL_BORDER = colorTokens.warning[300];
const SNOOZE_DURATION_LABEL_PREFIX = 'Snoozed for ';
const ICON_SIZE = 14;

/** Same as ConversationListItem name column */
const LABEL_FONT_SIZE = 14;

const GmailIcon: React.FC<{ size?: number }> = ({ size = ICON_SIZE }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ flexShrink: 0 }}
  >
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
  </svg>
);

function pickContrastText(hex: string): string {
  const raw = hex.replace('#', '');
  if (raw.length !== 6) return '#FFFFFF';
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.65 ? '#1F2937' : '#FFFFFF';
}

export const ConversationLabels: React.FC<ConversationLabelsProps> = ({
  labels,
  accountLabels,
  maxLabels = 2,
  sx,
}) => {
  if (!labels || labels.length === 0) {
    return null;
  }

  const enrichedLabels: ConversationLabel[] = labels.map((label) => {
    if (typeof label === 'string') {
      const isGmailLabel = label.startsWith('Gmail:');
      const isSnoozeDuration = label.startsWith(SNOOZE_DURATION_LABEL_PREFIX);
      const accountLabel = accountLabels?.find((al) => al.title === label);
      if (accountLabel && !isSnoozeDuration) return accountLabel;
      return {
        title: label,
        color: isSnoozeDuration
          ? SNOOZE_DURATION_LABEL_BG
          : isGmailLabel
            ? GMAIL_LABEL_COLOR
            : DEFAULT_LABEL_COLOR,
      };
    }
    return label;
  });

  const visibleLabels = enrichedLabels.slice(0, maxLabels);
  const remainingCount = enrichedLabels.length - visibleLabels.length;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        ...sx,
      }}
    >
      {visibleLabels.map((label, index) => {
        const isGmailLabel = label.title.startsWith('Gmail:');
        const isSnoozeDuration = label.title.startsWith(
          SNOOZE_DURATION_LABEL_PREFIX
        );
        const displayTitle = isGmailLabel
          ? label.title.replace('Gmail:', '').trim()
          : label.title;
        const labelColor = isSnoozeDuration
          ? SNOOZE_DURATION_LABEL_BG
          : isGmailLabel
            ? GMAIL_LABEL_COLOR
            : label.color || DEFAULT_LABEL_COLOR;
        const textColor = isSnoozeDuration
          ? SNOOZE_DURATION_LABEL_TEXT
          : pickContrastText(labelColor);
        const tooltipTitle = isGmailLabel
          ? 'Synced from Gmail'
          : label.title;

        const labelElement = (
          <Box
            key={label.id || `${displayTitle}-${index}`}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              minHeight: 28,
              px: 1.25,
              py: 0.25,
              borderRadius: '999px',
              backgroundColor: labelColor,
              border: isSnoozeDuration
                ? `1px solid ${SNOOZE_DURATION_LABEL_BORDER}`
                : undefined,
              boxSizing: 'border-box',
              maxWidth: 180,
              cursor: 'default',
            }}
          >
            {isGmailLabel && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: textColor,
                  flexShrink: 0,
                }}
              >
                <GmailIcon />
              </Box>
            )}
            <Box
              component="span"
              sx={{
                fontSize: `${LABEL_FONT_SIZE}px`,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: textColor,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayTitle}
            </Box>
          </Box>
        );

        return (
          <Tooltip
            key={label.id || `${displayTitle}-${index}`}
            title={tooltipTitle}
            arrow
            placement="top"
          >
            {labelElement}
          </Tooltip>
        );
      })}
      {remainingCount > 0 && (
        <Box
          component="span"
          sx={{
            fontSize: `${LABEL_FONT_SIZE}px`,
            fontWeight: 600,
            lineHeight: 1.3,
            color: colorTokens.semantic.text.tertiary,
            whiteSpace: 'nowrap',
          }}
        >
          +{remainingCount}
        </Box>
      )}
    </Box>
  );
};
